import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BoxMode, type BoxInfo } from '@lub0v/santa-utils';
import { Button, Checkbox, Select, Stack, Typography, BoxPicture, showDialog } from '@src/components';
import { closeDialog, useDialogOpen } from '@src/components/Dialog/Dialog';
import { Page } from '@src/components/Page/Page';
import { ThemedGameCard } from '@src/components/GameCard/ThemedGameCard';
import { gameAccessTier } from '@src/components/PremiumBadge/tierIdentity';
import { useDuplicateGameDialog } from '@src/pages/BoxesPage/DuplicateGameDialog';
import { useAppSelector } from '@src/state/hooks';
import { santaClient } from '@src/state/client';
import { DEFAULT_GAME_SEASON } from '@src/theme/season';
import { trackGameCopy, type GameCopySource } from '@src/utils/gameCopyAnalytics';
import './PreviousGameSuggestion.css';
import { newestRecentGame } from './recentGame';
import { readNewBoxDraft, saveNewBoxDraft } from './savedDraft';
import { BOX_NEW_PATH, boxOrganizerPath } from '@src/routing/routes';
import { trackRecentGame } from '@src/utils/recentGameAnalytics';

function isOlderGame(game: BoxInfo): boolean {
  const year = Number(game.box.year);
  return Number.isInteger(year) && year > 0 && year < new Date().getFullYear();
}

const preferenceKey = (userId: number) => `ss:hide-copy-suggestion:${userId}`;
function isHidden(userId: number): boolean {
  try { return localStorage.getItem(preferenceKey(userId)) === 'true'; } catch { return false; }
}

interface SuggestionProps { games: BoxInfo[]; userId: number; source?: GameCopySource }

function showSuggestion(title: string, closeLabel: string, props: SuggestionProps) {
  showDialog({ title, closeLabel, testId: 'previous-game-suggestion',
    children: <Suggestion {...props} /> });
}

function Suggestion({ games, userId, source = 'create_game' }: SuggestionProps) {
  const { t } = useTranslation();
  const copyGame = useDuplicateGameDialog(source);
  const trackedShown = useRef(false);
  useEffect(() => {
    if (trackedShown.current) return;
    trackedShown.current = true;
    trackGameCopy('game_copy_suggestion_shown', source, 'account');
  }, [source]);
  const [selected, setSelected] = useState(games[0].box.key);
  const [hide, setHide] = useState(false);
  const game = games.find((item) => item.box.key === selected) ?? games[0];
  const remember = (value: boolean) => {
    setHide(value);
    try {
      if (value) localStorage.setItem(preferenceKey(userId), 'true');
      else localStorage.removeItem(preferenceKey(userId));
    } catch { /* Optional preference. */ }
  };
  return <Stack gap={4}>
    <Typography as="p" role="body/base">{t('newBox.copySuggestion.body')}</Typography>
    {games.length > 1 && <Select label={t('newBox.copySuggestion.choose')} value={selected}
      options={games.map(({ box }) => ({ value: box.key, label: box.name }))} onValueChange={setSelected} />}
    <div className="previous-game-suggestion__card">
      <ThemedGameCard season={game.box.theme || DEFAULT_GAME_SEASON}
        picture={<BoxPicture picture={game.box.picture} logo={game.box.logo} season={game.box.theme || DEFAULT_GAME_SEASON} />}
        name={game.box.name}
        meta={[game.box.year, t('boxes.participants', { count: game.participantsNumber }), ...(game.isActiveDraw ? [t('boxes.drawn')] : [])]}
        premiumTier={gameAccessTier(game.box.features)}
        premiumLabel={game.box.isPremium ? t('newBox.appearance.premium') : undefined}
        testId="previous-game-card" />
    </div>
    <Typography as="p" role="meta/helper">{t('newBox.copySuggestion.later')}</Typography>
    <Checkbox checked={hide} onCheckedChange={remember} label={t('newBox.copySuggestion.hide')} />
    <div className="previous-game-suggestion__actions">
      <Button variant="secondary" onClick={closeDialog}>
        {t(source === 'game_visit' ? 'newBox.copySuggestion.continue' : 'newBox.copySuggestion.create')}
      </Button>
      <Button onClick={() => copyGame(game)}>
        {t('boxes.duplicate.title')}
      </Button>
    </div>
  </Stack>;
}

/** Check before mounting the wizard so guest dialogs and draft writes cannot race recovery. */
export function PreviousGameSuggestion({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const user = useAppSelector((state) => state.session.user);
  // Session changes while creating a game must not interrupt that same creation.
  const [entryUser] = useState(user);
  const [ready, setReady] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!entryUser || params.has('resume') || params.has('resumeCopy') || location.state?.resumedCopy) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Complete the external recovery gate immediately when this entry cannot have a recoverable game.
      setReady(true);
      return;
    }
    let cancelled = false;
    let opened = false;
    void santaClient.accountBoxes().then((result) => {
      if (cancelled) return;
      const allGames = result.ok ? result.data ?? [] : [];
      const recent = newestRecentGame(allGames);
      if (recent) {
        opened = true;
        showDialog({
          title: t('newBox.recentGame.title'),
          closeLabel: t('dialog.close'),
          testId: 'recent-game-suggestion',
          onCancel: () => navigate('/'),
          children: <RecentGameSuggestion game={recent} accountType={entryUser.isGuest ? 'guest' : 'account'}
            onCreate={() => {
              // Explicitly starting over must not restore the tab's old draft on reload.
              try { sessionStorage.removeItem('ss:new-box:current'); } catch { /* Optional storage. */ }
              closeDialog();
              setReady(true);
            }}
            onContinue={() => {
              trackRecentGame('recent_game_continued', entryUser.isGuest ? 'guest' : 'account');
              const saved = readNewBoxDraft(entryUser.id, recent.box.key);
              closeDialog();
              if (saved) {
                saveNewBoxDraft({ ...saved, step: 'picture', editingThemeFromReview: false });
                navigate(`${BOX_NEW_PATH}?resume=${encodeURIComponent(recent.box.key)}`);
              } else {
                navigate(`${boxOrganizerPath(recent.box.key)}?tab=appearance`);
              }
            }} />,
        });
        return;
      }
      setReady(true);
      if (entryUser.isGuest || isHidden(entryUser.id)) return;
      const games = allGames.filter((game) => game.isAdmin && isOlderGame(game) && game.box.mode !== BoxMode.QUICK);
      if (!games.length) return;
      opened = true;
      showSuggestion(t('newBox.copySuggestion.title'), t('dialog.close'), { games, userId: entryUser.id });
    }).catch(() => { if (!cancelled) setReady(true); });
    return () => { cancelled = true; if (opened) closeDialog(); };
  }, [entryUser, location.search, location.state, navigate, t]);
  // Keep the usual scenery behind the dialog without mounting the wizard,
  // whose effects would create a new draft before the user chooses.
  return ready ? children : <Page landscape="soft" testId="recent-game-background">{null}</Page>;
}

function RecentGameSuggestion({ game, accountType, onCreate, onContinue }: {
  game: BoxInfo; accountType: 'guest' | 'account'; onCreate: () => void; onContinue: () => void;
}) {
  const { t } = useTranslation();
  const tracked = useRef(false);
  useEffect(() => {
    if (tracked.current) return;
    tracked.current = true;
    trackRecentGame('recent_game_prompt_opened', accountType);
  }, [accountType]);
  return <Stack gap={4}>
    <Typography as="p" role="body/base">{t('newBox.recentGame.body')}</Typography>
    <Typography as="p" role="body/strong" testId="recent-game-name">{game.box.name}</Typography>
    <Button testId="recent-game-continue" onClick={onContinue}>{t('newBox.recentGame.continue')}</Button>
    <Button variant="secondary" testId="recent-game-create" onClick={onCreate}>{t('newBox.recentGame.create')}</Button>
  </Stack>;
}

/** Reuse the same suggestion and account-scoped preference when visiting an old game. */
export function PreviousGameVisitSuggestion({ info, enabled }: { info: BoxInfo | null; enabled: boolean }) {
  const { t } = useTranslation();
  const user = useAppSelector(state => state.session.user);
  const location = useLocation();
  const open = useDialogOpen();
  const shown = useRef(new Set<string>());
  useEffect(() => {
    if (!enabled || open || !user || user.isGuest || !info?.isAdmin ||
      info.box.mode === BoxMode.QUICK || !isOlderGame(info) ||
      new URLSearchParams(location.search).has('resumeCopy') || location.state?.resumedCopy || isHidden(user.id)) return;
    const visit = `${user.id}:${info.box.key}`;
    if (shown.current.has(visit)) return;
    shown.current.add(visit);
    showSuggestion(t('newBox.copySuggestion.title'), t('dialog.close'), {
      games: [info], userId: user.id, source: 'game_visit',
    });
  }, [enabled, open, user, info, location.search, location.state, t]);
  return null;
}
